import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { apiGet, apiPatch, apiDelete } from '../api/client.js';
import { useProjectScope } from '../lib/projectScope.js';
import { pillarLabel, fmtAgo } from '../lib/labels.js';
import { usePostApproval } from '../lib/usePostApproval.js';
import { PostThumb, TextHover } from '../components/PostPreviews.jsx';
import { TikTokOptionsButton } from '../components/TikTokOptions.jsx';

function fmtTime(et) {
  if (!et) return '—';
  const d = new Date(et);
  // Guard invalid dates: `new Date('nonsense')` is Invalid Date, and
  // .toLocaleString() on it returns the literal string "Invalid Date" WITHOUT
  // throwing, so the try/catch never fires. Fall back to the em-dash instead.
  if (Number.isNaN(d.getTime())) return '—';
  try {
    return d.toLocaleString('en-US', {
      timeZone: 'America/New_York', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return '—'; }
}

function truncate(str, n) {
  if (!str) return '';
  return str.length <= n ? str : str.slice(0, n) + '…';
}

/**
 * Inline reject form — shown below the card when the user clicks Reject.
 */
function RejectForm({ rejectM, onCancel }) {
  const [reason, setReason] = useState('');
  return (
    <div className="inbox-reject-form">
      <textarea
        className="input"
        placeholder="Reason for rejection (optional)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={2}
      />
      <div className="inbox-reject-actions">
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => rejectM.mutate(reason.trim() || undefined)}
          disabled={rejectM.isPending}
        >
          {rejectM.isPending ? 'Rejecting…' : 'Confirm Reject'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
      {rejectM.isError && (
        <p className="error-text">{rejectM.error?.message || 'Rejection failed'}</p>
      )}
    </div>
  );
}

/**
 * Single post card in the approval queue.
 */
function InboxCard({ post }) {
  const [rejecting, setRejecting] = useState(false);

  // Shared approval mutations (ADR-0029 P1). reject takes an optional reason.
  // Regenerate-with-feedback lives on the post detail page only (owner call
  // 2026-07-25: the inbox is for quick approve/reject decisions).
  const { approve, reject } = usePostApproval(post.id, { onReject: () => setRejecting(false) });

  const busy = approve.isPending;

  return (
    <div className="inbox-card">
      <div className="inbox-card-header">
        <div className="inbox-card-meta">
          <span className="pillar-badge">
            {pillarLabel(post.pillar_key)}
          </span>
          {post.project_name && (
            <span className="inbox-project">{post.project_name}</span>
          )}
          <span className="muted inbox-age">
            {fmtAgo(post.created_at)}
          </span>
        </div>
        <Link to={`/posts/${post.id}`} className="btn btn-ghost btn-icon" title="Open post" aria-label="Open post">
          ↗
        </Link>
      </div>

      <div className="inbox-card-body">
        <PostThumb post={post} linkTo={`/posts/${post.id}`} className="inbox-thumb" />
        <div className="inbox-card-text">
          <TextHover post={post}>
            {post.title && (
              <h3 className="inbox-card-title">{post.title}</h3>
            )}
            {(post.caption || post.story_text) && (
              <p className="inbox-card-caption muted">
                {truncate(post.caption || post.story_text, 320)}
              </p>
            )}
          </TextHover>
        </div>
      </div>

      <div className="inbox-card-footer">
        <span className="inbox-suggested-time">
          Suggested: {fmtTime(post.suggested_posting_time_et)}
        </span>
        <div className="inbox-actions">
          <TikTokOptionsButton post={post} />
          <button
            type="button"
            className="btn btn-primary btn-icon"
            onClick={() => approve.mutate()}
            disabled={busy}
            title="Approve"
            aria-label="Approve"
          >
            {approve.isPending ? '…' : '✓'}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-icon btn-icon-danger"
            onClick={() => setRejecting((r) => !r)}
            disabled={busy}
            title="Reject"
            aria-label="Reject"
          >
            ✕
          </button>
        </div>
      </div>

      {approve.isError && (
        <p className="error-text">{approve.error?.message || 'Approval failed'}</p>
      )}

      {rejecting && (
        <RejectForm
          rejectM={reject}
          onCancel={() => setRejecting(false)}
        />
      )}
    </div>
  );
}

const INTENT_COLORS = {
  high:   'var(--color-danger)',   // red — high intent, action needed fast
  medium: 'var(--color-warn)',     // cyan — medium
  low:    'var(--color-queued)',   // zinc — low
};

/**
 * Task #50 — deep link to the actual comment on the platform (the social-
 * side counterpart of "View in Markaii"). Built from the publication's
 * stored permalink_url:
 *   instagram: {permalink}c/{comment_id}/   (IG's comment-permalink path)
 *   facebook:  {permalink}?comment_id={id}  (highlights the comment)
 * FB comment ids arrive composite ("{post}_{comment}") — the link wants the
 * tail; IG ids have no underscore so split().pop() is the id itself.
 * LinkedIn keeps its dedicated "View post ↗" link; X has no comment harvest.
 */
function platformCommentUrl(lead) {
  if (lead.source_type === 'dm') return null;
  const commentTail = (lead.external_comment_id || '').split('_').pop();
  if (lead.platform === 'instagram' && lead.permalink_url) {
    const base = lead.permalink_url.endsWith('/') ? lead.permalink_url : `${lead.permalink_url}/`;
    return commentTail ? `${base}c/${encodeURIComponent(commentTail)}/` : base;
  }
  if (lead.platform === 'facebook') {
    const base = lead.permalink_url
      || (lead.external_post_id ? `https://www.facebook.com/${lead.external_post_id}` : null);
    if (!base) return null;
    if (!commentTail) return base;
    return `${base}${base.includes('?') ? '&' : '?'}comment_id=${encodeURIComponent(commentTail)}`;
  }
  return null;
}

function intentLabel(score) {
  if (score == null) return { label: '—', color: 'var(--color-text-faint)' };
  if (score >= 70) return { label: `${score} — High`, color: INTENT_COLORS.high };
  if (score >= 40) return { label: `${score} — Medium`, color: INTENT_COLORS.medium };
  return { label: `${score} — Low`, color: INTENT_COLORS.low };
}

/**
 * Single lead card in the leads queue.
 */
function LeadCard({ lead }) {
  const qc = useQueryClient();

  const approveReply = useMutation({
    mutationFn: () => apiPatch(`/leads/${lead.id}`, { reply_approval_status: 'approved' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  });
  const skipReply = useMutation({
    mutationFn: () => apiPatch(`/leads/${lead.id}`, { reply_approval_status: 'skipped' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  });
  const approveDm = useMutation({
    mutationFn: () => apiPatch(`/leads/${lead.id}`, { dm_approval_status: 'approved' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  });
  const skipDm = useMutation({
    mutationFn: () => apiPatch(`/leads/${lead.id}`, { dm_approval_status: 'skipped' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  });
  // Custom-inbox message delete (Meta messaging App Review — the review tests
  // read + reply + DELETE). Removes the conversation from the Inbox entirely;
  // distinct from "skip", which only hides it. Hard delete → confirm first.
  const deleteLead = useMutation({
    mutationFn: () => apiDelete(`/leads/${lead.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  });

  // Edit-before-posting (task #42): pencil opens a textarea over the draft;
  // Save persists it, "Save & approve" persists AND approves in one PATCH.
  const [editing, setEditing] = useState(null); // 'reply' | 'dm' | null
  const [draftText, setDraftText] = useState('');
  const saveDraft = useMutation({
    mutationFn: ({ field, approve }) => apiPatch(`/leads/${lead.id}`, {
      [field]: draftText.trim(),
      ...(approve
        ? (field === 'reply_draft'
            ? { reply_approval_status: 'approved' }
            : { dm_approval_status: 'approved' })
        : {}),
    }),
    onSuccess: () => { setEditing(null); qc.invalidateQueries({ queryKey: ['leads'] }); },
  });
  const startEdit = (which) => {
    setDraftText(which === 'reply' ? (lead.reply_draft || '') : (lead.dm_draft || ''));
    setEditing(which);
  };

  const draftEditor = (field) => (
    <div style={{ marginTop: 6 }}>
      <textarea
        className="input" rows={4} autoFocus
        style={{ width: '100%' }}
        value={draftText} maxLength={2000}
        onChange={(e) => setDraftText(e.target.value)}
      />
      <div className="inbox-actions" style={{ marginTop: 6 }}>
        <button type="button" className="btn btn-primary btn-icon"
          disabled={saveDraft.isPending || !draftText.trim()}
          onClick={() => saveDraft.mutate({ field, approve: true })}
          title={field === 'dm_draft' && isInboundDm ? 'Save & send' : 'Save & approve'}
          aria-label={field === 'dm_draft' && isInboundDm ? 'Save and send' : 'Save and approve'}>
          {saveDraft.isPending ? '…' : '✓'}
        </button>
        <button type="button" className="btn btn-ghost btn-icon"
          disabled={saveDraft.isPending || !draftText.trim()}
          onClick={() => saveDraft.mutate({ field, approve: false })}
          title="Save draft (stays pending)" aria-label="Save draft">
          💾
        </button>
        <button type="button" className="btn btn-ghost btn-icon"
          onClick={() => setEditing(null)} disabled={saveDraft.isPending}
          title="Cancel editing" aria-label="Cancel editing">
          ✕
        </button>
      </div>
      {saveDraft.isError && (
        <p className="error-text">{saveDraft.error?.message || 'Save failed'}</p>
      )}
    </div>
  );

  const busy = approveReply.isPending || skipReply.isPending || approveDm.isPending || skipDm.isPending;
  const intent = intentLabel(lead.intent_score);
  // Which decisions does this card actually need? With auto-reply ON the
  // public reply is often already 'sent' and only the DM awaits a human.
  const replyPending = lead.reply_approval_status === 'pending_approval' && lead.reply_draft;
  const dmPending = lead.dm_approval_status === 'pending_approval' && lead.dm_draft;
  // Inbound Meta DM (task #26): the dm_draft is the ANSWER and approving it
  // makes leadSender actually deliver it via the platform API.
  const isInboundDm = lead.source_type === 'dm';

  // Task #33 — lead info + open-the-message links.
  const SIGNAL_LABEL = { pain_point: 'pain point', question: 'question', competitor: 'competitor', buying_signal: 'buying signal' };
  const signalChips = Object.entries(lead.intent_signals || {})
    .filter(([, v]) => v)
    .map(([k]) => SIGNAL_LABEL[k] || k.replace(/_/g, ' '));
  const conversationLink = isInboundDm
    ? (lead.platform === 'facebook' && lead.brand_account_id
        ? `https://business.facebook.com/latest/inbox/all?asset_id=${encodeURIComponent(lead.brand_account_id)}`
        : lead.platform === 'instagram'
          ? 'https://www.instagram.com/direct/inbox/'
          : null)
    : null;

  // Proper English ordinals incl. 11th/12th/13th and 21st/22nd/23rd.
  const nth = (n) => { const s = ['th', 'st', 'nd', 'rd']; const v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };

  return (
    <div className="inbox-card">
      <div className="inbox-card-header">
        <div className="inbox-card-meta">
          <span className="pillar-badge">{lead.platform}</span>
          {isInboundDm && <span className="pillar-badge">DM</span>}
          {isInboundDm && lead.unsent_at && (
            <span className="lead-signal-chip" title="The sender unsent a message">↩ unsent</span>
          )}
          {lead.project_name && (
            <span className="inbox-project">{lead.project_name}</span>
          )}
          <span className="muted inbox-age">{fmtAgo(lead.created_at)}</span>
          {Number(lead.person_interactions) > 1 && (
            <span className="lead-signal-chip" title="This person has engaged the brand before — see the Prospects tab for their history">
              {nth(Number(lead.person_interactions))} interaction
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: intent.color, fontSize: '0.8rem', fontWeight: 600 }}>
            {intent.label}
          </span>
          {isInboundDm && (
            <button
              type="button"
              className="btn btn-ghost btn-icon btn-icon-danger"
              onClick={() => {
                if (window.confirm('Delete this conversation from your inbox? This cannot be undone.')) deleteLead.mutate();
              }}
              disabled={deleteLead.isPending}
              title="Delete conversation" aria-label="Delete conversation"
            >
              {deleteLead.isPending ? '…' : '🗑'}
            </button>
          )}
        </div>
      </div>

      <p className="inbox-card-title" style={{ fontSize: '0.9rem' }}>
        {lead.platform_username
          ? (isInboundDm ? lead.platform_username : `@${lead.platform_username}`)
          : (isInboundDm ? 'Messenger user'
              : lead.platform === 'linkedin' ? 'LinkedIn member' : 'A commenter')}
        {isInboundDm && <span className="muted"> · sent you a direct message</span>}
        {!isInboundDm && lead.source_post_title && (
          <span className="muted"> on "{truncate(lead.source_post_title, 50)}"</span>
        )}
        {' '}
        {conversationLink && (
          <a className="lead-open-link" href={conversationLink} target="_blank" rel="noreferrer">
            Open conversation ↗
          </a>
        )}
        {!isInboundDm && lead.platform === 'linkedin' && lead.external_post_id && (
          <a className="lead-open-link" target="_blank" rel="noreferrer"
            href={`https://www.linkedin.com/feed/update/${encodeURIComponent(lead.external_post_id)}/`}>
            View post ↗
          </a>
        )}
        {platformCommentUrl(lead) && (
          <a className="lead-open-link" target="_blank" rel="noreferrer"
            title="Open this comment on the platform"
            href={platformCommentUrl(lead)}>
            View on {lead.platform} ↗
          </a>
        )}
        {!isInboundDm && lead.source_post_id && (
          <Link className="lead-open-link" to={`/posts/${lead.source_post_id}`}>
            View in Markaii
          </Link>
        )}
      </p>

      {signalChips.length > 0 && (
        <p className="lead-signals">
          {signalChips.map((s) => <span key={s} className="lead-signal-chip">{s}</span>)}
        </p>
      )}

      {lead.unsent_at ? (
        <p className="inbox-card-caption muted" style={{ fontStyle: 'italic' }}>
          ↩ The sender unsent their message.
        </p>
      ) : lead.source_text && (
        <p className="inbox-card-caption muted" style={{ fontStyle: 'italic' }}>
          "{truncate(lead.source_text, 220)}"
        </p>
      )}

      {lead.reply_draft && (
        <div className="lead-reply-preview">
          <span className="muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {replyPending ? 'Suggested reply' : 'Reply (already posted)'}
          </span>
          {editing === 'reply' ? draftEditor('reply_draft') : (
            <>
              <p style={{ margin: '4px 0 0', fontSize: '0.9rem', whiteSpace: 'pre-line' }}>{lead.reply_draft}</p>
              {replyPending && (
                <div className="inbox-actions" style={{ marginTop: 8 }}>
                  <button type="button" className="btn btn-primary btn-icon"
                    onClick={() => approveReply.mutate()} disabled={busy}
                    title="Approve reply" aria-label="Approve reply">
                    {approveReply.isPending ? '…' : '✓'}
                  </button>
                  <button type="button" className="btn btn-ghost btn-icon"
                    onClick={() => startEdit('reply')} disabled={busy}
                    title="Edit before posting" aria-label="Edit reply">
                    ✏️
                  </button>
                  <button type="button" className="btn btn-ghost btn-icon btn-icon-danger"
                    onClick={() => skipReply.mutate()} disabled={busy}
                    title="Skip reply" aria-label="Skip reply">
                    ✕
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {dmPending && (
        <div className="lead-reply-preview">
          <span className="muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {isInboundDm
              ? 'Suggested answer (approve = Markaii sends it as the brand)'
              : 'Suggested DM (high-intent lead — always needs your approval)'}
          </span>
          {editing === 'dm' ? draftEditor('dm_draft') : (
            <>
              {/* NEVER truncate a draft awaiting approval — the owner must read
                  exactly what will be sent (owner-reported "looks cut" 2026-07-26). */}
              <p style={{ margin: '4px 0 0', fontSize: '0.9rem', whiteSpace: 'pre-line' }}>{lead.dm_draft}</p>
              {/* Inbound Meta DMs (source_type 'dm') are answered via the API on
                  approve. Comment-source DM drafts stay manual: X/LinkedIn have
                  no page→member messaging API, so the human copies + sends. */}
              <div className="inbox-actions" style={{ marginTop: 8 }}>
                <button type="button" className="btn btn-primary btn-icon"
                  onClick={() => approveDm.mutate()} disabled={busy}
                  title={isInboundDm ? 'Approve & send' : 'Approve DM'}
                  aria-label={isInboundDm ? 'Approve and send' : 'Approve DM'}>
                  {approveDm.isPending ? '…' : '✓'}
                </button>
                <button type="button" className="btn btn-ghost btn-icon"
                  onClick={() => startEdit('dm')} disabled={busy}
                  title="Edit before sending" aria-label="Edit DM">
                  ✏️
                </button>
                <button type="button" className="btn btn-ghost btn-icon"
                  onClick={() => navigator.clipboard?.writeText(lead.dm_draft)}
                  title="Copy DM text" aria-label="Copy DM text">
                  📋
                </button>
                <button type="button" className="btn btn-ghost btn-icon btn-icon-danger"
                  onClick={() => skipDm.mutate()} disabled={busy}
                  title="Skip DM" aria-label="Skip DM">
                  ✕
                </button>
                {!isInboundDm && lead.platform === 'instagram' && lead.platform_username && (
                  <a className="btn btn-ghost btn-icon" target="_blank" rel="noreferrer"
                    title={`Open @${lead.platform_username} on Instagram`} aria-label="Open Instagram profile"
                    href={`https://www.instagram.com/${encodeURIComponent(lead.platform_username)}/`}>
                    ↗
                  </a>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {(approveReply.isError || skipReply.isError || approveDm.isError || skipDm.isError) && (
        <p className="error-text">
          {approveReply.error?.message || skipReply.error?.message
            || approveDm.error?.message || skipDm.error?.message || 'Action failed'}
        </p>
      )}
    </div>
  );
}

/**
 * ProspectCard (task #53) — one PERSON across all their interactions with
 * the org's brands: counts, best score, signal union, lifecycle status
 * with icon actions, and an expandable interaction history.
 */
const LIFECYCLE_ACTIONS = [
  { id: 'contacted', icon: '🤝', label: 'Mark as contacted' },
  { id: 'trial',     icon: '🧪', label: 'Mark as on trial' },
  { id: 'customer',  icon: '⭐', label: 'Mark as customer' },
  { id: 'not_a_fit', icon: '🚫', label: 'Mark as not a fit' },
];

function ProspectCard({ prospect }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const statusM = useMutation({
    mutationFn: (lifecycle_status) => apiPatch('/prospects', {
      platform: prospect.platform,
      person_key: prospect.person_key,
      lifecycle_status,
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['prospects'] }),
  });

  const signals = [
    prospect.has_buying_signal && 'buying signal',
    prospect.has_question && 'question',
    prospect.has_pain_point && 'pain point',
    prospect.has_competitor && 'competitor',
  ].filter(Boolean);
  const hot = prospect.has_buying_signal || (prospect.best_score || 0) >= 70 || prospect.interactions >= 2;
  const history = prospect.history || [];

  return (
    <div className="inbox-card">
      <div className="inbox-card-header">
        <div className="inbox-card-meta">
          <span className="pillar-badge">{prospect.platform}</span>
          <b>{prospect.platform_username ? `@${prospect.platform_username}` : 'Unknown handle'}</b>
          <span className="muted inbox-age">
            {prospect.interactions} interaction{prospect.interactions === 1 ? '' : 's'} · last {fmtAgo(prospect.last_touch)}
          </span>
          {(prospect.project_names || []).map((n) => (
            <span key={n} className="inbox-project">{n}</span>
          ))}
          {hot && <span className="lead-signal-chip" title="Buying signal, high intent score, or repeat engagement">🔥 hot</span>}
        </div>
        <span className="muted" style={{ fontSize: '0.8rem' }}>
          {prospect.best_score != null ? `best score ${prospect.best_score}` : 'unscored'}
          {' · '}{prospect.lifecycle_status.replace(/_/g, ' ')}
        </span>
      </div>

      {signals.length > 0 && (
        <p className="lead-signals">
          {signals.map((s) => <span key={s} className="lead-signal-chip">{s}</span>)}
        </p>
      )}

      <div className="inbox-actions">
        {LIFECYCLE_ACTIONS.map((a) => (
          <button key={a.id} type="button"
            className={`btn btn-ghost btn-icon${prospect.lifecycle_status === a.id ? ' active' : ''}`}
            title={a.label} aria-label={a.label}
            disabled={statusM.isPending || prospect.lifecycle_status === a.id}
            onClick={() => statusM.mutate(a.id)}>
            {a.icon}
          </button>
        ))}
        <button type="button" className="btn btn-ghost btn-icon"
          title={open ? 'Hide history' : 'Show history'}
          aria-label={open ? 'Hide history' : 'Show history'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}>
          {open ? '▴' : '▾'}
        </button>
      </div>

      {open && (
        <div className="lead-reply-preview">
          {history.map((h) => (
            <div key={h.id} style={{ margin: '6px 0' }}>
              <span className="muted" style={{ fontSize: '0.75rem' }}>
                {fmtAgo(h.created_at)} · {h.source_type === 'dm' ? 'DM' : 'comment'}
                {h.project_name ? ` · ${h.project_name}` : ''}
                {h.intent_score != null ? ` · score ${h.intent_score}` : ''}
              </span>
              {h.source_text && (
                <p className="muted" style={{ fontStyle: 'italic', margin: '2px 0' }}>"{truncate(h.source_text, 200)}"</p>
              )}
              {h.answer && (
                <p style={{ margin: '2px 0', fontSize: '0.85rem' }}>↳ {truncate(h.answer, 200)}</p>
              )}
              {platformCommentUrl({ ...h, platform: prospect.platform }) && (
                <a className="lead-open-link" target="_blank" rel="noreferrer"
                  href={platformCommentUrl({ ...h, platform: prospect.platform })}>
                  View on {prospect.platform} ↗
                </a>
              )}
              {h.source_post_id && (
                <>{' '}<Link className="lead-open-link" to={`/posts/${h.source_post_id}`}>View in Markaii</Link></>
              )}
            </div>
          ))}
          {!history.length && <p className="muted">No interactions recorded.</p>}
        </div>
      )}

      {statusM.isError && (
        <p className="error-text">{statusM.error?.body?.error || statusM.error?.message}</p>
      )}
    </div>
  );
}

/**
 * /inbox — approval queue for posts in 'review' state + leads awaiting reply approval.
 * Sorted oldest-first (FIFO) so nothing stagnates at the bottom.
 * Auto-refreshes every 30 seconds.
 */
const LEAD_FILTERS = [
  { id: 'pending',  label: 'Pending to answer' },
  { id: 'answered', label: 'Answered' },
  { id: 'skipped',  label: 'Skipped' },
  { id: 'reactors', label: 'Reactors' },
];

export default function Inbox() {
  // Tab + lead filter live in the URL (?tab=leads&filter=answered) so a
  // refresh or a shared link keeps the exact view (owner request 2026-07-31).
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = ['leads', 'prospects'].includes(searchParams.get('tab'))
    ? searchParams.get('tab') : 'posts';
  const leadFilter = LEAD_FILTERS.some((f) => f.id === searchParams.get('filter'))
    ? searchParams.get('filter') : 'pending';
  const setTab = (t) => setSearchParams(t === 'posts' ? {} : { tab: t }, { replace: true });
  const setLeadFilter = (f) => setSearchParams(
    f === 'pending' ? { tab: 'leads' } : { tab: 'leads', filter: f }, { replace: true });
  const [scope] = useProjectScope();

  const postsQ = useQuery({
    queryKey: ['inbox', scope],
    queryFn: ({ signal }) => apiGet(`/posts?state=review&limit=50&sort=asc${scope ? `&project=${encodeURIComponent(scope)}` : ''}`, { signal }),
    refetchInterval: 30_000,
  });

  const leadsQ = useQuery({
    queryKey: ['leads', scope],
    queryFn: ({ signal }) => apiGet(`/leads?limit=100${scope ? `&project_key=${encodeURIComponent(scope)}` : ''}`, { signal }),
    refetchInterval: 30_000,
  });

  // Prospects are org-wide people, not per-project rows — no scope filter.
  const prospectsQ = useQuery({
    queryKey: ['prospects'],
    queryFn: ({ signal }) => apiGet('/prospects', { signal }),
    enabled: tab === 'prospects',
    refetchInterval: 60_000,
  });

  const posts = postsQ.data?.posts || [];
  const allLeads = leadsQ.data?.leads || [];
  // Needs a human: reply awaiting approval (auto-reply OFF or safety-held)
  // OR a drafted DM awaiting approval (DMs are always human-gated).
  const leads = allLeads.filter(
    (l) => (l.reply_approval_status === 'pending_approval' && l.reply_draft)
        || (l.dm_approval_status === 'pending_approval' && l.dm_draft)
  );
  // Visibility for the automatic loop: what was answered on your behalf.
  // 'approved'/'sending' DM answers ride along so an approved answer never
  // vanishes from the UI during the minutes before the send cron delivers it.
  const answered = allLeads
    .filter((l) => l.reply_approval_status === 'sent'
        || (l.source_type === 'dm' && ['approved', 'sending', 'sent'].includes(l.dm_approval_status)))
    .slice(0, 50);
  // Reactors: lowest-intent engagement, no reply path (LinkedIn gates invites
  // and DMs to humans) — a checklist for the two manual clicks that convert
  // them: invite-to-follow the page, or connect personally.
  const reactors = allLeads
    .filter((l) => l.source_type === 'reaction')
    .slice(0, 50);
  // Everything else — skipped/dismissed conversations, kept findable.
  const skipped = allLeads.filter(
    (l) => !leads.includes(l) && !answered.includes(l) && l.source_type !== 'reaction'
  ).slice(0, 50);
  const leadListByFilter = { pending: leads, answered, skipped, reactors };
  const postCount = posts.length;
  const leadCount = leads.length;

  const prospects = prospectsQ.data?.prospects || [];
  const q     = tab === 'posts' ? postsQ : tab === 'prospects' ? prospectsQ : leadsQ;
  const leadItems = leadListByFilter[leadFilter] || [];
  const count = tab === 'posts' ? postCount : tab === 'prospects' ? prospects.length : leadItems.length;

  return (
    <div className="inbox-page">
      <header className="page-header">
        <div>
          <h1>
            Inbox
            {(postCount + leadCount) > 0 && (
              <span className="inbox-badge">{postCount + leadCount}</span>
            )}
          </h1>
          <p className="muted">
            Posts and leads waiting for your approval.
          </p>
        </div>
        <Link to="/posts" className="btn btn-ghost">
          All Posts
        </Link>
      </header>

      <div className="inbox-tabs">
        <button
          type="button"
          className={`inbox-tab${tab === 'posts' ? ' active' : ''}`}
          onClick={() => setTab('posts')}
        >
          Posts {postCount > 0 && <span className="inbox-badge-sm">{postCount}</span>}
        </button>
        <button
          type="button"
          className={`inbox-tab${tab === 'leads' ? ' active' : ''}`}
          onClick={() => setTab('leads')}
        >
          Leads {leadCount > 0 && <span className="inbox-badge-sm">{leadCount}</span>}
        </button>
        <button
          type="button"
          className={`inbox-tab${tab === 'prospects' ? ' active' : ''}`}
          onClick={() => setTab('prospects')}
        >
          Prospects
        </button>
      </div>

      {tab === 'prospects' && (
        <div style={{ marginTop: 4 }}>
          <p className="muted" style={{ margin: '0 0 12px', fontSize: '0.85rem' }}>
            Every person who engaged your brands, hottest first. Track them toward
            becoming customers: 🤝 contacted · 🧪 trial · ⭐ customer · 🚫 not a fit.
          </p>
          {prospectsQ.isLoading && <div className="loading">Loading prospects…</div>}
          {prospectsQ.isError && <p className="error-text">{prospectsQ.error?.message}</p>}
          {!prospectsQ.isLoading && prospects.length === 0 && (
            <p className="muted">No prospects yet — they appear as soon as someone comments or messages your brands.</p>
          )}
          <div className="inbox-list">
            {prospects.map((p) => (
              <ProspectCard key={`${p.platform}:${p.person_key}`} prospect={p} />
            ))}
          </div>
        </div>
      )}

      {tab === 'leads' && (
        <div className="filter-row" style={{ marginBottom: 14 }}>
          {LEAD_FILTERS.map((f) => (
            <button key={f.id} type="button"
              className={`filter-chip ${leadFilter === f.id ? 'active' : ''}`}
              onClick={() => setLeadFilter(f.id)}>
              {f.label} {leadListByFilter[f.id]?.length > 0 && `(${leadListByFilter[f.id].length})`}
            </button>
          ))}
        </div>
      )}

      {/* The prospects section above renders its own loading/error/empty
          states — these shared blocks are for the posts/leads tabs only. */}
      {tab !== 'prospects' && q.isLoading && (
        <div className="loading-state">Loading…</div>
      )}

      {tab !== 'prospects' && q.isError && (
        <div className="error-state">
          Failed to load: {q.error?.message}
        </div>
      )}

      {tab !== 'prospects' && !q.isLoading && !q.isError && count === 0 && (
        <div className="empty-state">
          {tab === 'posts' || leadFilter === 'pending' ? (
            <>
              <p>Nothing pending — you're all caught up.</p>
              {tab === 'posts' ? (
                <p className="muted">Posts move here automatically after pillars fire and slides render.</p>
              ) : (
                <p className="muted">Leads appear here after comments are harvested from published posts.</p>
              )}
              <Link to="/projects" className="btn">View Projects</Link>
            </>
          ) : (
            <p className="muted">Nothing in "{LEAD_FILTERS.find((f) => f.id === leadFilter)?.label}" yet.</p>
          )}
        </div>
      )}

      {count > 0 && tab === 'posts' && (
        <div className="inbox-list">
          {posts.map((post) => <InboxCard key={post.id} post={post} />)}
        </div>
      )}
      {count > 0 && tab === 'leads' && leadFilter === 'pending' && (
        <div className="inbox-list">
          {leads.map((lead) => <LeadCard key={lead.id} lead={lead} />)}
        </div>
      )}

      {/* Automatic-answer visibility: what the brand replied on your behalf.
          Read-only history — auto-reply already posted these (or you approved
          them earlier); shown so the automatic loop is never invisible. */}
      {tab === 'leads' && (leadFilter === 'answered' || leadFilter === 'skipped') && leadItems.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {leadFilter === 'answered' && (
            <p className="muted" style={{ margin: '0 0 12px', fontSize: '0.85rem' }}>
              Replies posted as the brand — automatically, or after your approval.
            </p>
          )}
          <div className="inbox-list">
            {leadItems.map((l) => (
              <div key={l.id} className="inbox-card">
                <div className="inbox-card-header">
                  <div className="inbox-card-meta">
                    <span className="pillar-badge">{l.platform}</span>
                    {l.project_name && <span className="inbox-project">{l.project_name}</span>}
                    <span className="muted inbox-age">{fmtAgo(l.reply_sent_at || l.dm_sent_at || l.updated_at)}</span>
                  </div>
                  <span className="muted" style={{ fontSize: '0.8rem' }}>
                    {leadFilter === 'skipped' ? '✕ skipped'
                      : l.source_type === 'dm' && l.dm_approval_status !== 'sent' ? '⏳ sending…'
                      : '✓ answered'}
                  </span>
                </div>
                <p className="inbox-card-title" style={{ fontSize: '0.88rem', margin: '2px 0' }}>
                  {l.platform_username
                    ? (l.source_type === 'dm' ? l.platform_username : `@${l.platform_username}`)
                    : (l.platform === 'linkedin' ? 'LinkedIn member'
                        : l.source_type === 'dm' ? 'Messenger user' : 'A commenter')}
                  <span className="muted">
                    {l.source_type === 'dm' ? ' · direct message' : ' · comment'}
                    {l.source_post_title ? ` on "${truncate(l.source_post_title, 45)}"` : ''}
                  </span>
                  {platformCommentUrl(l) && (
                    <>
                      {' '}
                      <a className="lead-open-link" target="_blank" rel="noreferrer"
                        title="Open this comment thread on the platform"
                        href={platformCommentUrl(l)}>
                        View on {l.platform} ↗
                      </a>
                    </>
                  )}
                </p>
                {l.source_text && (
                  <p className="inbox-card-caption muted" style={{ fontStyle: 'italic', margin: '4px 0' }}>
                    "{truncate(l.source_text, 160)}"
                  </p>
                )}
                {(l.source_type === 'dm' ? l.dm_draft : l.reply_draft) && (
                  <div className="lead-reply-preview">
                    <span className="muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {l.source_type === 'dm' ? 'Brand answered by DM' : 'Brand replied'}
                    </span>
                    <p style={{ margin: '4px 0 0', fontSize: '0.9rem' }}>
                      {truncate(l.source_type === 'dm' ? l.dm_draft : l.reply_draft, 240)}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reactors checklist — the two clicks LinkedIn reserves for humans. */}
      {tab === 'leads' && leadFilter === 'reactors' && reactors.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <p className="muted" style={{ margin: '0 0 12px', fontSize: '0.85rem' }}>
            People who reacted to a post. LinkedIn has no API for inviting or messaging them —
            open the post, <b>click its reaction count</b>, and as the page's admin you'll see
            <b> Invite to follow</b> next to eligible reactors (or connect personally).
          </p>
          <div className="inbox-list">
            {reactors.map((l) => (
              <div key={l.id} className="inbox-card">
                <div className="inbox-card-header">
                  <div className="inbox-card-meta">
                    <span className="pillar-badge">{l.platform}</span>
                    {l.project_name && <span className="inbox-project">{l.project_name}</span>}
                    <span className="muted inbox-age">{fmtAgo(l.created_at)}</span>
                  </div>
                  <span className="muted" style={{ fontSize: '0.8rem' }}>👍 reacted</span>
                </div>
                <p className="inbox-card-caption muted" style={{ margin: '4px 0' }}>
                  <b style={{ color: 'var(--color-text)' }}>
                    {l.platform_username ? `@${l.platform_username}` : 'A LinkedIn member'}
                  </b>
                  {' '}reacted to {l.source_post_title ? `"${truncate(l.source_post_title, 60)}"` : 'a post'}
                  {!l.platform_username && l.platform === 'linkedin' && (
                    <span className="muted"> — LinkedIn hides reactor names from all tools; the post's reaction list shows them</span>
                  )}
                </p>
                {/* Straight to THE post (owner request 2026-07-31 — the
                    admin posts-list made them hunt). Invite to follow lives
                    in the post's reactions modal for page admins; the admin
                    list stays as a small fallback. */}
                <div className="inbox-actions">
                  {l.platform === 'linkedin' && l.external_post_id && (
                    <a className="btn btn-ghost btn-icon" target="_blank" rel="noreferrer"
                      title="Open this exact post acting AS the page — click its reaction count to see who reacted and invite them"
                      aria-label="Open the post on LinkedIn as the page"
                      // actorCompanyId opens the post in act-as-page context
                      // (owner-verified URL format 2026-07-31) — required for
                      // the admin-only actions to surface on the post.
                      href={`https://www.linkedin.com/feed/update/${encodeURIComponent(l.external_post_id)}/${l.brand_account_id ? `?actorCompanyId=${encodeURIComponent((l.brand_account_id || '').split(':').pop())}` : ''}`}>
                      ↗
                    </a>
                  )}
                  {l.platform === 'linkedin' && l.brand_account_id && (
                    <a className="btn btn-ghost btn-icon" target="_blank" rel="noreferrer"
                      title="Fallback: the page-admin posts list, if the invite buttons don't show on the post"
                      aria-label="Open the LinkedIn page-admin posts list"
                      href={`https://www.linkedin.com/company/${encodeURIComponent((l.brand_account_id || '').split(':').pop())}/admin/page-posts/published/`}>
                      ⚙
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="inbox-footer muted">
        {tab === 'prospects'
          ? `${count} prospect${count === 1 ? '' : 's'} · all projects · auto-refreshes every 60s`
          : count > 0
            ? `${count} item${count === 1 ? '' : 's'} pending · auto-refreshes every 30s`
            : 'Auto-refreshes every 30s'}
      </div>
    </div>
  );
}
